import { jsPDF } from "jspdf";
import bgImage from "@/assets/bg.png";
import signImage from "@/assets/sign.png";

// US Letter, in points. The bg.png aspect ratio (1871x2421 ≈ 0.773) matches Letter.
const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN_L = 70;
const MARGIN_R = 70;
const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R;

// Fixed copy that is NOT filled from the form (kept static for now).
const FIXED = {
  folioRef: "FAZP001589902025",
  folioNum: "FOLIO N°001589902026",
  ordenId: "ID#7407",
  descripcion: "ASEGURADO Y APROBADO",
  directorName: "Andrew N. Ferguson",
  directorRole: "Director Financiero",
};

/**
 * Loads a webpack asset URL into a base64 data URL via a canvas so jsPDF can embed it.
 */
function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Generates the "Certificación de Legalidad y Procedencia de Fondos" PDF and
 * triggers a browser download. All variable fields come from `data`.
 *
 * @param {Object} data
 * @param {string} data.fecha          e.g. "17/06/2026"
 * @param {string} data.nombre         e.g. "Negoescu Rodica" (Apellido Nombre)
 * @param {string} data.documento      e.g. "X6684662B"
 * @param {string} data.nacionalidad   e.g. "ESPAÑOLA"
 * @param {string} data.capital        e.g. "$91,501 USD"
 * @param {string} data.costo          e.g. "$319 USD"
 * @param {string} data.montoTabla     e.g. "$ 91,501"
 * @param {string} data.beneficiario   e.g. "Rodica Negoescu" (firma)
 */
export async function generateCertificate(data) {
  const bgData = await loadImageAsDataUrl(bgImage);
  const signData = await loadImageAsDataUrl(signImage);

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setLineHeightFactor(1.15);
  doc.addImage(bgData, "PNG", 0, 0, PAGE_W, PAGE_H);

  let y = 150;

  const lineHeight = (size) => size * 1.15;

  // Writes wrapped, justified text and advances the cursor. Returns new y.
  const paragraph = (text, { size = 9.5, bold = false, gap = 6, align = "justify" } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, CONTENT_W);
    doc.text(lines, MARGIN_L, y, { maxWidth: CONTENT_W, align, lineHeightFactor: 1.15 });
    y += lines.length * lineHeight(size) + gap;
  };

  // Parrafo con tramos en negrita intercalados. jsPDF no soporta estilos inline
  // dentro de un mismo doc.text(), asi que medimos y dibujamos palabra por
  // palabra, cambiando de fuente segun el tramo y repartiendo el sobrante entre
  // los espacios para conservar el justificado.
  const richParagraph = (segments, { size = 9.5, gap = 6 } = {}) => {
    doc.setFontSize(size);

    const widthOf = (word, bold) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      return doc.getTextWidth(word);
    };

    doc.setFont("helvetica", "normal");
    const spaceW = doc.getTextWidth(" ");

    // Una "palabra" puede mezclar estilos: la coma que sigue a un nombre en
    // negrita pertenece a la misma palabra pero se dibuja en redonda. Por eso
    // cada palabra guarda una lista de tramos. Dos tramos se pegan solo si no
    // hay espacio entre ellos, asi la puntuacion nunca queda suelta.
    const words = [];
    let prevEndsWithSpace = true;
    segments.forEach(({ text, bold }) => {
      const raw = String(text || "");
      if (!raw) {
        // Un dato vacio no debe dejar la puntuacion siguiente separada.
        prevEndsWithSpace = false;
        return;
      }
      const startsWithSpace = /^\s/.test(raw);
      const tokens = raw.split(/\s+/).filter(Boolean);
      tokens.forEach((token, index) => {
        const part = {
          text: token,
          bold: Boolean(bold),
          w: widthOf(token, bold),
        };
        const glued =
          index === 0 && !startsWithSpace && !prevEndsWithSpace && words.length > 0;
        if (glued) {
          const last = words[words.length - 1];
          last.parts.push(part);
          last.w += part.w;
        } else {
          words.push({ parts: [part], w: part.w });
        }
      });
      prevEndsWithSpace = tokens.length ? /\s$/.test(raw) : true;
    });

    // Corte de lineas: una palabra mas larga que el ancho util va sola.
    const lines = [];
    let current = [];
    let currentW = 0;
    words.forEach((item) => {
      const extra = current.length ? spaceW + item.w : item.w;
      if (current.length && currentW + extra > CONTENT_W) {
        lines.push(current);
        current = [item];
        currentW = item.w;
      } else {
        current.push(item);
        currentW += extra;
      }
    });
    if (current.length) lines.push(current);

    lines.forEach((line, index) => {
      const isLast = index === lines.length - 1;
      const wordsW = line.reduce((acc, item) => acc + item.w, 0);
      // La ultima linea no se estira: se deja con espacios normales.
      const spacing =
        !isLast && line.length > 1
          ? (CONTENT_W - wordsW) / (line.length - 1)
          : spaceW;

      let x = MARGIN_L;
      line.forEach((item) => {
        item.parts.forEach((part) => {
          doc.setFont("helvetica", part.bold ? "bold" : "normal");
          doc.text(part.text, x, y);
          x += part.w;
        });
        x += spacing;
      });
      y += lineHeight(size);
    });

    y += gap;
    doc.setFont("helvetica", "normal");
  };

  // Bullet item: bold lead label inline, then the rest of the paragraph below.
  const bullet = (label, body) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text("•", MARGIN_L, y);
    const labelX = MARGIN_L + 12;
    const labelLines = doc.splitTextToSize(label, CONTENT_W - 12);
    doc.text(labelLines, labelX, y);
    y += labelLines.length * lineHeight(9.5) + 2;
    if (body) {
      doc.setFont("helvetica", "normal");
      const bodyLines = doc.splitTextToSize(body, CONTENT_W - 12);
      doc.text(bodyLines, labelX, y, { align: "justify", maxWidth: CONTENT_W - 12, lineHeightFactor: 1.15 });
      y += bodyLines.length * lineHeight(9.5) + 6;
    }
  };

  // --- Meta block (top-left, below the printed letterhead) ---
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(data.fecha || "", MARGIN_L, y);
  y += 12;
  doc.text(FIXED.folioRef, MARGIN_L, y);
  y += 12;
  doc.text(FIXED.folioNum, MARGIN_L, y);
  y += 26;

  // --- Title ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12.5);
  doc.text("CERTIFICACIÓN DE LEGALIDAD Y PROCEDENCIA DE FONDOS", PAGE_W / 2, y, {
    align: "center",
  });
  y += 26;

  // --- Recipient ---
  paragraph("Señor (a):", { size: 9.5, gap: 1 });
  paragraph(data.nombre || "", { size: 9.5, bold: true, gap: 12 });

  // --- Intro paragraph ---
  // Nombre, documento y nacionalidad van en negrita dentro del parrafo.
  richParagraph(
    [
      { text: "Por medio de la presente, se certifica que el beneficiario " },
      { text: data.nombre || "", bold: true },
      { text: ", identificado con documento de identidad " },
      { text: data.documento || "", bold: true },
      { text: ", de nacionalidad " },
      { text: data.nacionalidad || "", bold: true },
      {
        text:
          ", ha sido objeto de un proceso exhaustivo de rastreo de movimientos en la " +
          "bolsa de valores. Este proceso ha resultado en la identificación de un capital " +
          `recuperado con un valor total de ${data.capital || ""}. Los fondos mencionados ` +
          "serán movilizados y resguardados en una entidad bancaria custodio de carácter " +
          "internacional, garantizando su integridad y seguridad hasta el momento del desembolso.",
      },
    ],
    { gap: 10 }
  );

  // --- Considerations ---
  paragraph("Consideraciones Importantes", { size: 10, bold: true, gap: 8 });

  bullet(
    `Costo de Inicio del Proceso: ${data.costo || ""}`,
    "Este costo cubre los servicios administrativos, legales y de auditoría necesarios para llevar a cabo el proceso de recuperación de capital de manera eficiente y conforme a la normativa vigente."
  );
  bullet(
    "Desembolso de Capital:",
    "El desembolso de los fondos se generará a una cuenta destino que deberá ser proporcionada por el beneficiario. Este procedimiento se llevará a cabo tras la confirmación del ingreso del capital. Esta medida asegura que se obtenga la autorización correspondiente para la circulación de moneda extranjera, cumpliendo así con todas las regulaciones locales e internacionales."
  );
  bullet(
    "Documentación Requerida:",
    "El beneficiario deberá aportar la documentación requerida para la verificación de titularidad, vinculación y autenticidad de la cuenta destino designada para la recepción de los fondos, así como cualquier otro documento adicional exigido por las autoridades competentes en el marco de la normativa vigente."
  );
  bullet(
    "Tiempos de Procesamiento:",
    "El proceso de movilización y congelación de los fondos puede llevar un tiempo estimado de 72 horas hábiles, dependiendo de la complejidad del caso y de las regulaciones aplicables."
  );
  bullet(
    "Responsabilidades del Beneficiario:",
    "El beneficiario se compromete a seguir todas las indicaciones proporcionadas por nuestra entidad y a colaborar en la entrega de la información necesaria para facilitar el proceso."
  );

  paragraph(
    "Este certificado es un documento oficial que valida el proceso legal llevado a cabo y la " +
      "autenticidad de los fondos recuperados. Se recomienda al beneficiario conservar este " +
      "documento en caso de que se requiera para futuras gestiones ante autoridades financieras o legales.",
    { gap: 8 }
  );

  // --- Page 2: transactions table + signatures ---
  doc.addPage();
  doc.addImage(bgData, "PNG", 0, 0, PAGE_W, PAGE_H);
  y = 170;

  paragraph(
    "A continuación, se emite la lista de las transacciones en un informe detallado:",
    { gap: 16 }
  );

  drawTable(doc, y, {
    ordenId: FIXED.ordenId,
    fecha: (data.fecha || "").replace(/\//g, "-"),
    descripcion: FIXED.descripcion,
    monto: data.montoTabla || "",
  });
  y += 60;

  // --- Signatures ---
  const sigY = Math.max(y + 40, 712);

  // Director's handwritten signature, centered over the left line, resting on it.
  const signW = 110;
  const signH = signW * (121 / 191);
  const leftLineCenter = MARGIN_L + 80;
  doc.addImage(signData, "PNG", leftLineCenter - signW / 2, sigY - signH + 6, signW, signH);

  doc.setDrawColor(120);
  doc.setLineWidth(0.6);
  doc.line(MARGIN_L, sigY, MARGIN_L + 160, sigY);
  doc.line(PAGE_W - MARGIN_R - 160, sigY, PAGE_W - MARGIN_R, sigY);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(FIXED.directorName, MARGIN_L, sigY + 12);
  doc.text(data.beneficiario || "", PAGE_W - MARGIN_R, sigY + 12, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(FIXED.directorRole, MARGIN_L, sigY + 24);
  doc.text("Beneficiario", PAGE_W - MARGIN_R, sigY + 24, { align: "right" });

  const safeName = (data.beneficiario || "certificado").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
  doc.save(`certificado_${safeName}.pdf`);
}

function drawTable(doc, top, row) {
  const cols = [
    { key: "ordenId", label: "NÚMERO ORDEN", w: 110 },
    { key: "fecha", label: "FECHA", w: 100 },
    { key: "descripcion", label: "DESCRIPCIÓN", w: 162 },
    { key: "monto", label: "MONTO", w: 100 },
  ];
  const rowH = 22;
  let x = MARGIN_L;

  // Header row
  doc.setFillColor(13, 27, 62);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  cols.forEach((c) => {
    doc.rect(x, top, c.w, rowH, "F");
    doc.text(c.label, x + c.w / 2, top + rowH / 2 + 3, { align: "center" });
    x += c.w;
  });

  // Data row
  x = MARGIN_L;
  doc.setTextColor(33, 37, 41);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setDrawColor(200);
  doc.setLineWidth(0.5);
  cols.forEach((c) => {
    doc.rect(x, top + rowH, c.w, rowH, "S");
    doc.text(String(row[c.key] || ""), x + c.w / 2, top + rowH + rowH / 2 + 3, {
      align: "center",
    });
    x += c.w;
  });

  doc.setTextColor(0, 0, 0);
}
